import { FUNCTIONALITY, ISurvey } from '../../constants/types';
import { useCurrentInvestigation } from '../../hooks';
import { getSubmissionEditHistoryService } from '../../services';
import { decryptData, getInvestigationRawKey } from '../../utils';
import { AmendmentHistoryView, useAmendmentHistory } from './useAmendmentHistory';
import { DecryptValue, SubmissionEditHistoryRow, toAmendmentEntries } from './submissionEditHistory';

/**
 * Decrypts a stored survey value with the investigation key (legacy CryptoJS
 * ciphertext, self-contained). The history keeps no nonce, so an AES-GCM value
 * cannot be read back from it: decryptData answers "" and the value shows as
 * encrypted. The key is derived only when an encrypted value actually appears.
 */
function investigationDecryptor(investigation: any): DecryptValue {
    let key: string | null = null;
    return (ciphertext) => {
        key ??= getInvestigationRawKey(investigation.uuid, investigation.encryptedKeyUsed, investigation.keyResearcherInvestigation);
        const plain = key ? decryptData(ciphertext, key) : '';
        return plain === '' ? null : plain;
    };
}

/**
 * SKIN-602 — a submission's edit history, ready for AmendmentHistory.
 * Asked only where the study has WEB_CLINICAL_CORRECTION (the API refuses the
 * read otherwise); an undefined submission id (e.g. a printout) asks nothing.
 * A new `version` reads it again (SKIN-576: a photo was re-filed from the page).
 */
export function useSubmissionEditHistory(uuidInvestigation: string | undefined, submissionId: number | undefined, survey: ISurvey, version = 0): AmendmentHistoryView {
    const { currentInvestigation, hasFunctionality } = useCurrentInvestigation();
    const enabled = hasFunctionality(FUNCTIONALITY.WEB_CLINICAL_CORRECTION) && !!uuidInvestigation && !!submissionId;
    return useAmendmentHistory(enabled ? `${uuidInvestigation}/${survey.uuid}/${submissionId}/${version}` : null, async () => {
        const data: { editHistory?: SubmissionEditHistoryRow[] } = await getSubmissionEditHistoryService(uuidInvestigation, submissionId);
        return toAmendmentEntries(data.editHistory ?? [], survey, investigationDecryptor(currentInvestigation));
    });
}
